import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import { getSurveyIcon, toast } from '@utilities';
import {
  createModalSurvey,
  readInstantSurveyType,
  validateInstantSurveyPrompts,
} from '@api/instant_survey';

import { generateTemporarySurveyTitle } from '../../../../../../pages/CreateInstantSurvey/utilities/helpers';

import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../../../../../pages/CreateInstantSurvey/components/Panelists/utilities/helpers';

import PreBuildModal from '../../../../../PreBuildModal';

import { transformPromptAnswers } from './utilities/helpers';

const ModalBuildSurvey = ({ isModalOpen, route, setIsModalOpen }) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalPromptRequirements, setModalPromptRequirements] = useState({});
  const [modalPrompts, setModalPrompts] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [surveyTitle, setSurveyTitle] = useState('');
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});

  const navigate = useNavigate();
  const panel = useSelector((state) => state?.user?.data?.panel);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) {
      setSurveyTitle('');
      setSelectedAnswers({});
      setTransformedAnswersMap({});
      setActivePrompt(null);
      setModalPrompts([]);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;

    const fetchPrompts = async () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const data = await readInstantSurveyType({ id: route?.path });
        const prompts = data?.prompts || [];
        const modalPrompts = prompts.filter((p) => p.isModalPrompt);
        setModalPromptRequirements(data?.modalPromptRequirements?.[0]);
        setModalPrompts(modalPrompts);
        setActivePrompt(modalPrompts[0] || null);
      } catch (error) {
        console.error('Error fetching survey prompts:', error);
        toast('Failed to load prompts.', { status: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompts();

    return () => abortControllerRef.current?.abort();
  }, [isModalOpen, route?.path]);

  const saveSurveyDraft = useCallback(
    async (promptAnswers) => {
      try {
        return await createModalSurvey({
          country: panel?.abbrev?.toLowerCase(),
          instantSurveyType: route?.path,
          promptAnswers,
          title: surveyTitle.trim() || generateTemporarySurveyTitle(),
        });
      } catch (error) {
        console.error('Save error:', error);
        toast('Failed to save your survey draft.', { status: 'error' });
        return null;
      }
    },
    [route?.path, surveyTitle]
  );

  const validateSurveyById = useCallback(async (id) => {
    try {
      await validateInstantSurveyPrompts({ id });
      return true;
    } catch (error) {
      console.error('Validation error:', error);
      return false;
    }
  }, []);
  const buildSurveyDraft = useCallback(async () => {
    try {
      const promptAnswers = transformPromptAnswers(Object.values(transformedAnswersMap).flat());

      const survey = await saveSurveyDraft(promptAnswers);
      if (!survey) return null;

      const isValid = await validateSurveyById(survey.id);
      return isValid ? survey : null;
    } catch (error) {
      console.error('Build error:', error);
      toast('An error occurred while preparing your survey.', { status: 'error' });
      return null;
    }
  }, [transformedAnswersMap, saveSurveyDraft, validateSurveyById]);

  const handleBuildSurvey = useCallback(async () => {
    setIsBuilding(true);
    const survey = await buildSurveyDraft();
    if (!survey) {
      toast('There was an error building your survey.', { status: 'error' });
      setIsBuilding(false);
      return;
    }

    navigate(`/dashboard/create-survey/${route?.path}?ref=${survey.id}&modal=true`);
    setIsModalOpen(false);
  }, [buildSurveyDraft, navigate, route?.path, setIsModalOpen]);

  const isNonEmpty = (value) => (Array.isArray(value) ? value?.length > 0 : !isEmpty(value));
  const requiredFields = modalPrompts.filter((prompt) => prompt?.isRequiredForModal);
  const hasRequiredPrompts =
    modalPromptRequirements?.prompts?.length > 0
      ? modalPromptRequirements.prompts.some((promptKey) =>
          isNonEmpty(transformedAnswersMap?.[promptKey])
        )
      : true;

  const hasAllRequiredFields = requiredFields?.every(({ promptKey }) =>
    isNonEmpty(transformedAnswersMap?.[promptKey])
  );

  const validPercent = (values) => {
    const [light, medium, heavy] = values;
    const nums = [light, medium, heavy].map(Number);
    return (
      nums.every((num) => Number.isFinite(num) && num >= 0) &&
      nums.reduce((sum, num) => sum + num, 0) === 100
    );
  };

  const validPair = (values) => {
    const [light, heavy] = values;
    const lightNum = Number(light);
    const heavyNum = Number(heavy);
    if (!Number.isFinite(lightNum) || !Number.isFinite(heavyNum)) return false;
    if (lightNum < 0 || heavyNum < 0) return false;
    return heavyNum > lightNum;
  };

  const hasValidPurchaseVolume = useMemo(() => {
    const purchaseVolumeAnswers = transformedAnswersMap['purchase_volume'];
    if (!purchaseVolumeAnswers) return true;

    return purchaseVolumeAnswers.every((answer) => {
      if (!answer?.values?.length) return false;

      const isPercent = answer.attributeId?.toLowerCase?.()?.includes('percent');
      return isPercent ? validPercent(answer.values) : validPair(answer.values);
    });
  }, [transformedAnswersMap?.purchase_volume]);

  const hasValidPurchaseShift = useMemo(() => {
    const purchaseShiftAnswers = transformedAnswersMap['purchase_shift'];
    if (!purchaseShiftAnswers) return true;

    return purchaseShiftAnswers.every((answer) => {
      if (!answer?.values?.length) return false;
      return validPair(answer.values);
    });
  }, [transformedAnswersMap?.purchase_shift]);

  const isBuildDisabled = !(
    hasRequiredPrompts &&
    hasAllRequiredFields &&
    hasValidPurchaseVolume &&
    hasValidPurchaseShift
  );

  return (
    <PreBuildModal
      activePrompt={activePrompt}
      getPillTooltip={getPromptPillTooltip}
      getPillValue={getPromptPillValue}
      handleBuild={handleBuildSurvey}
      headerIcon={getSurveyIcon(route?.path)}
      isBuildDisabled={isBuildDisabled}
      isBuilding={isBuilding}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      name={surveyTitle}
      prompts={modalPrompts}
      routeLabel={route?.label}
      searchPrompts={route?.searchPrompts}
      selectedAnswers={selectedAnswers}
      setActivePrompt={setActivePrompt}
      setIsModalOpen={setIsModalOpen}
      setName={setSurveyTitle}
      setSelectedAnswers={setSelectedAnswers}
      setTransformedAnswersMap={setTransformedAnswersMap}
      transformedAnswersMap={transformedAnswersMap}
      transformPromptSelections={transformPromptSelections}
      type="survey"
    />
  );
};

ModalBuildSurvey.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  route: PropTypes.object.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
};

export default ModalBuildSurvey;
